import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, Observable, catchError, of, tap } from 'rxjs';
import { TenantConfig, DEFAULT_TENANT_CONFIG } from './tenant-config.model';
import { ThemeService } from '../theme/theme.service';
import { TenantFeatureService } from './tenant-feature.service';
import { environment } from '../../../environments/environment';

/**
 * Lädt und verwaltet die Mandanten-Konfiguration (TenantConfig).
 *
 * Die Konfiguration wird einmalig beim App-Start von der Backend-API geladen.
 * Ist die API nicht erreichbar, greift ein Fallback auf die DEFAULT_TENANT_CONFIG.
 * Nach erfolgreichem Laden wird das Tenant-Branding automatisch über den
 * ThemeService auf :root angewendet.
 *
 * Verwendung im APP_INITIALIZER:
 * ```ts
 * { provide: APP_INITIALIZER,
 *   useFactory: (s: TenantConfigService) => () => s.loadConfig(),
 *   deps: [TenantConfigService], multi: true }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class TenantConfigService {

  /** API-Endpunkt für die Tenant-Konfiguration. */
  private readonly CONFIG_URL = `${environment.apiUrl}/api/tenant/config`;

  private readonly http = inject(HttpClient);
  private readonly themeService = inject(ThemeService);
  private readonly titleService = inject(Title);
  private readonly featureService = inject(TenantFeatureService);

  /**
   * Aktuell geladene Mandanten-Konfiguration als Observable.
   * Startet mit der DEFAULT_TENANT_CONFIG als Initialwert.
   */
  readonly config$ = new BehaviorSubject<TenantConfig>(DEFAULT_TENANT_CONFIG);

  /**
   * Lädt die Mandanten-Konfiguration vom Backend.
   * Bei Fehler (API nicht erreichbar, Netzwerkfehler) wird die
   * DEFAULT_TENANT_CONFIG verwendet – kein Absturz der App.
   * Nach dem Laden wird das Branding automatisch angewendet.
   *
   * @returns Observable<TenantConfig> – wird genau einmal emittiert.
   */
  loadConfig(tenantId?: string): Observable<TenantConfig> {
    const url = tenantId ? `${this.CONFIG_URL}?tenantId=${tenantId}` : this.CONFIG_URL;
    return this.http.get<TenantConfig>(url).pipe(
      catchError(() => {
        // Fallback-Konfiguration bei Netzwerkfehler oder nicht erreichbarer API
        console.warn('[TenantConfigService] API nicht erreichbar – Fallback-Config wird verwendet.');
        return of(DEFAULT_TENANT_CONFIG);
      }),
      tap(config => {
        this.config$.next(config);
        // Branding sofort nach dem Laden auf :root anwenden
        if (config.branding) {
          this.themeService.applyTenantBranding(config.branding);
        }
        // Browser-Titel aus TenantConfig setzen
        if (config.name) {
          this.titleService.setTitle(`${config.name} – Bauagent.ai`);
        }
        // Feature-Toggles aus der Config initialisieren
        this.featureService.initialisierenAusConfig(config);
      })
    );
  }

  /**
   * Gibt die aktuell geladene Konfiguration synchron zurück.
   * Nützlich in Services, die keine Observable-Kette benötigen.
   *
   * @returns Die aktuelle TenantConfig.
   */
  getConfig(): TenantConfig {
    return this.config$.getValue();
  }

  /**
   * Gibt den Anzeigenamen des Mandanten zurück.
   *
   * @returns Mandantenname (z. B. "LIGNA Systems").
   */
  getTenantName(): string {
    return this.config$.getValue().name;
  }

  /**
   * Gibt die Branding-Daten des Mandanten zurück.
   * Nützlich für die Logo-Komponente.
   *
   * @returns TenantBranding-Objekt mit Farben und Logo-SVG.
   */
  getBranding() {
    return this.config$.getValue().branding;
  }

  /**
   * Speichert die aktualisierte Mandanten-Konfiguration im Backend.
   * Nach erfolgreichem Speichern wird die lokale Config aktualisiert
   * und das Branding neu angewendet.
   *
   * @param config Die zu speichernde TenantConfig.
   * @returns Observable<void>
   */
  updateConfig(config: TenantConfig): Observable<void> {
    return this.http.put<void>(this.CONFIG_URL, config).pipe(
      tap(() => {
        this.config$.next(config);
        if (config.branding) {
          this.themeService.applyTenantBranding(config.branding);
        }
        if (config.name) {
          this.titleService.setTitle(`${config.name} – Bauagent.ai`);
        }
      })
    );
  }
}
